import { z } from 'zod';

import { adminRoadmapSessionQuestionSchema, adminRoadmapSummarySchema } from './admin-questions';
import { adminEntitlementSummarySchema } from './subscription';

/**
 * PRE-* admin "Users" explorer: a real learner's full picture — profile +
 * onboarding answers, every assessment/practice session with the questions they
 * saw and the answers they gave, and the roadmaps generated for them. Powers the
 * Users tab so an operator can inspect quality end-to-end for one person.
 */

/** One row in the users list. */
export const adminUserSummarySchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  createdAt: z.string(),
  /** Onboarding persona summary, when the user completed onboarding. */
  experienceLevel: z.string().nullable(),
  goal: z.string().nullable(),
  roadmapCount: z.number().int(),
  /** Mirrored RC plan; `free` when no active entitlement (PRE-247). */
  planId: z.string(),
  entitlementActive: z.boolean(),
  entitlementExpiresAt: z.string().nullable(),
});
export type AdminUserSummary = z.infer<typeof adminUserSummarySchema>;

export const adminUsersResponseSchema = z.object({ items: z.array(adminUserSummarySchema) });
export type AdminUsersResponse = z.infer<typeof adminUsersResponseSchema>;

/** Full onboarding profile (persona) for a user. */
export const adminUserProfileSchema = z.object({
  experienceLevel: z.string(),
  goal: z.string(),
  aiUsagePattern: z.array(z.string()),
  weakAreas: z.array(z.string()),
  aiTools: z.array(z.string()),
  onboardingCompletedAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type AdminUserProfile = z.infer<typeof adminUserProfileSchema>;

/** A session (assessment/practice) with its questions + the user's answers. */
export const adminUserSessionSchema = z.object({
  id: z.string(),
  sessionType: z.string(),
  status: z.string(),
  createdAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  questions: z.array(adminRoadmapSessionQuestionSchema),
});
export type AdminUserSession = z.infer<typeof adminUserSessionSchema>;

/**
 * A MEASURED weak skill (PRE-194): a skill whose running mastery — recency-
 * weighted `concept_mastery` across the user's graded assessment + practice
 * answers (knowledge-tracing lite, PRE-160) — is below the roadmap's weak-skill
 * threshold. This is the same signal the roadmap targets, so the admin view and
 * the roadmap agree. Distinct from the self-declared onboarding `weakAreas`.
 */
export const adminMeasuredWeakSkillSchema = z.object({
  skillId: z.string(),
  slug: z.string(),
  title: z.string(),
  category: z.string(),
  /** 0-100 recency-weighted concept mastery (lower = weaker). */
  mastery: z.number(),
  /** How many graded reports back this skill's mastery (signal confidence). */
  count: z.number().int(),
});
export type AdminMeasuredWeakSkill = z.infer<typeof adminMeasuredWeakSkillSchema>;

/** Full user detail: identity + profile + sessions (Q&A) + roadmaps. */
export const adminUserDetailSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    emailVerified: z.boolean(),
    createdAt: z.string(),
  }),
  profile: adminUserProfileSchema.nullable(),
  sessions: z.array(adminUserSessionSchema),
  roadmaps: z.array(adminRoadmapSummarySchema),
  /**
   * Measured weak skills from graded answers (mastery < roadmap threshold),
   * weakest first — the same knowledge-tracing source the roadmap uses.
   */
  measuredWeakSkills: z.array(adminMeasuredWeakSkillSchema),
  /** The weak-skill mastery threshold in effect (for display context). */
  weakSkillThreshold: z.number(),
  /** Total graded reports backing the mastery signal (data-sufficiency hint). */
  gradedReportCount: z.number().int(),
  /** Mirrored subscription / entitlement (PRE-247). */
  entitlement: adminEntitlementSummarySchema,
});
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;
